/**
 * APT362 (client, 1 Nov 2026): "showing wrong framework compared to the general settings". The Statutory screen printed the period's
 * framework CODE (`FRS102-1A`) where Report Settings says "FRS 102 (full)". Only three rule packs exist (FRS102-1A, FRS105 and NONSTAT), and the
 * period takes its pack from the saved taxonomy: FRS 105 gets the FRS105 pack, every other taxonomy the FRS102-1A pack (APT350). So the screen
 * names the framework the way the company's settings do and shows the pack that applies beneath it, and says so when a period that cannot change
 * (finalised, locked, with the client for approval) still holds a different framework from the company's current setting.
 */

export interface FrameworkInput {
  /** The period's framework code, as the API sends it (FRS102-1A, FRS105, NONSTAT). */
  frameworkCode: string;
  accountsTypeCode: string;
  /** The company's saved taxonomy code (FRS102_1A, FRS102, FRS105, UKGAAP, IFRS) and its name from the pick list, when they could be read. */
  taxonomyCode: string | null;
  taxonomyName: string | null;
  /** Why the period is read-only (the API's own text), or null for an open period. */
  lockedReason: string | null;
}

export interface FrameworkText {
  /** The line shown, e.g. "Framework for this period: FRS 102 (full)". */
  title: string;
  /** A smaller line under it, or null. */
  detail: string | null;
}

/** The names the Use Taxonomy list carries (db/seed/31), for when the list could not be read. */
const TAXONOMY_NAMES: Record<string, string> = {
  FRS102_1A: 'FRS 102 Section 1A (small entities)',
  FRS102: 'FRS 102 (full)',
  FRS105: 'FRS 105 (micro-entities)',
  UKGAAP: 'UK GAAP',
  IFRS: 'IFRS',
};

const upper = (value: string | null | undefined) => (value ?? '').trim().toUpperCase();

export function taxonomyDisplayName(code: string | null | undefined, listName: string | null | undefined): string | null {
  if (listName && listName.trim()) return listName.trim();
  const key = upper(code);
  return TAXONOMY_NAMES[key] ?? (code && code.trim() ? code.trim() : null);
}

/** The pack a taxonomy takes for a company or LLP: FRS 105 its own, every other taxonomy the FRS 102 Section 1A pack (as ap.usp_Period_SyncFramework). */
export function frameworkOfTaxonomy(taxonomyCode: string | null | undefined): 'FRS105' | 'FRS102-1A' {
  return upper(taxonomyCode) === 'FRS105' ? 'FRS105' : 'FRS102-1A';
}

/** What a pack is called to a person. */
function packName(frameworkCode: string): string {
  switch (upper(frameworkCode)) {
    case 'FRS102-1A':
      return 'FRS 102 Section 1A';
    case 'FRS105':
      return 'FRS 105 (micro-entities)';
    case 'NONSTAT':
      return 'non-statutory accounts';
    default:
      return frameworkCode;
  }
}

const rawLine = (input: FrameworkInput) => `Framework for this period: ${input.frameworkCode} · ${input.accountsTypeCode}`;

export function describeFramework(input: FrameworkInput): FrameworkText {
  const taxonomyName = taxonomyDisplayName(input.taxonomyCode, input.taxonomyName);
  const framework = upper(input.frameworkCode);
  // An unincorporated client has no taxonomy, and an unread setting cannot be compared: the screen says what it always said.
  if (framework === 'NONSTAT' || !taxonomyName || !input.taxonomyCode?.trim()) {
    return { title: rawLine(input), detail: null };
  }

  const pack = `${input.frameworkCode} · ${input.accountsTypeCode}`;
  if (frameworkOfTaxonomy(input.taxonomyCode) === framework) {
    return {
      title: `Framework for this period: ${taxonomyName}`,
      detail: `Disclosure rules applied: the ${packName(framework)} rules (${pack})`,
    };
  }

  // The period holds a different pack from the one the company's current setting gives.
  const reason = input.lockedReason ?? '';
  let why: string;
  if (/finalised|filed/i.test(reason)) {
    why = 'A finalised period keeps the framework it was finalised under.';
  } else if (reason) {
    why = 'A period keeps its framework while it is locked or with the client for approval; it follows the setting once it is open again and the settings are saved.';
  } else {
    why = 'Save the Company Info settings again to bring this period in line.';
  }
  return {
    title: `Framework for this period: ${packName(framework)} (${pack})`,
    detail: `The company's current setting is ${taxonomyName}. ${why}`,
  };
}
